import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import { updateChoice } from "areas/product/store/actions"
import {
  ChoiceOption,
  OptionTitle,
  OptionPrice,
} from "areas/product/wizard-options/components"
import { isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import displayOptionDescription from "helpers/product/display-option-description"
import displayOptionPrice from "helpers/product/display-option-price"
import { useNavigation } from "helpers/use-navigation"

import MetalIcon from "./choice/metal-icon"

const MetalOptions = ({ data, selectedValue, setChoice, surcharge }) => {
  const keyboardProps = useNavigation({ direction: "both" })

  return (
    <div
      aria-label={`${data.heading.title} Options`}
      className="tablet:px-6 desktop:grid-cols-4 tablet:gap-6 gap-18 grid auto-rows-fr grid-cols-3"
      role="radiogroup"
    >
      {data.options.map((metal) => {
        const selected = selectedValue === metal.value
        return (
          <ChoiceOption
            aria-checked={selected}
            className="tablet:mb-12 mb-0 flex-col border-2 p-4 pb-8"
            data-cy={`product-wizard-option-sku-${metal.value}`}
            data-is-option="true"
            data-is-selected={selected}
            key={`metal-${metal.value}`}
            onClick={() => setChoice(data.pref, metal)}
            role="radio"
            tabIndex={selected ? 0 : -1}
            {...keyboardProps}
          >
            <MetalIcon metal={metal} />
            <OptionTitle className="line-clamp-2 text-center">
              {metal.title}
            </OptionTitle>
            <OptionPrice>
              {to_currency(metal.display_price + surcharge)}
            </OptionPrice>
          </ChoiceOption>
        )
      })}
    </div>
  )
}

MetalOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  surcharge: PropTypes.number,
}

const InternalMetalSelectionStatic = ({
  data,
  description,
  headingTitle,
  hidePrice,
  price,
  selectedValue,
  setChoice,
  surcharge,
}) => {
  if (!data || data.disabled) return null

  return (
    <div data-cy={`wizard-accordion-${data.pref}`}>
      <div className="tablet:pl-6 tablet:pr-12 flex justify-between bg-white pt-10">
        <div className="flex h-32 flex-col items-start">
          <h2 className="text-14 tablet:font-bold tablet:tracking-half m-0 font-semibold uppercase text-black">
            Choose Your {headingTitle}
          </h2>
          <p className="text-grey-35 text-12 tablet:text-grey-65 tablet:font-normal tablet:tracking-half font-semibold">
            {description}
          </p>
        </div>

        {!hidePrice && (
          <p
            className="text-14 tracking-half flex items-center font-bold"
            data-ge-parentproduct-base-price
          >
            {price}
          </p>
        )}
      </div>

      <div className="pb-16 pt-10">
        <FocusScope>
          <MetalOptions
            data={data}
            selectedValue={selectedValue}
            setChoice={setChoice}
            surcharge={surcharge}
          />
        </FocusScope>
      </div>
    </div>
  )
}

InternalMetalSelectionStatic.propTypes = {
  data: PropTypes.object,
  description: PropTypes.string,
  headingTitle: PropTypes.string,
  hidePrice: PropTypes.bool,
  price: PropTypes.string,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  surcharge: PropTypes.number,
}

const mapStateToProps = (state, ownProps) => {
  const data = state.wizard.data[ownProps.pref]
  const selected = state.choices[ownProps.pref]
  const metalsIncludeSurchargeFor = state.wizard.metalsIncludeSurchargeFor

  const headingTitle = data?.heading?.title || ""

  const surcharge = metalsIncludeSurchargeFor.reduce((accu, curr) => {
    let surchargeFor = state.choices[curr]
    return accu + (surchargeFor?.display_price || surchargeFor?.price || 0)
  }, 0)

  return {
    data,
    description: displayOptionDescription(data, state, ownProps),
    headingTitle,
    hidePrice: metalsIncludeSurchargeFor.includes(ownProps.pref),
    price: displayOptionPrice(state, ownProps),
    selectedValue: selected?.value || "",
    surcharge,
  }
}

const mapDispatchToProps = (dispatch) => ({
  setChoice: (pref, metal) => {
    if (!isTestMode) {
      dataLayer.push({
        event: "selected-metal-option",
      })
    }
    dispatch(updateChoice(pref, metal, true))
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalMetalSelectionStatic)
